import { css, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js';
import { useEffect } from 'haunted'
import { LitHauntedElement } from '../mixins/lit-haunted-element';
import { ReportsAPI } from '../modules/mng-custom-reports/WebMethods';
import { FileReferenceAPI, FileReferenceClientStatus, FileReferenceMetadata } from './file-io/FileReference';
import { stylesBootstrap } from '../styles/bootstrap';
import { IntervalController } from '../controllers/interval-controller';
import { isNullishOrWhitespace } from '../functions/nullish';

type RunnerStatus = 'idle' | 'running' | 'completed' | 'failed'

@customElement('ep-report-runner')
export class EPReportRunner extends LitHauntedElement {

  @property({ attribute: 'api-origin', reflect: false })
  apiOrigin: string = window.origin;

  @property({ attribute: 'definition', reflect: true })
  definition?: string = '';

  @property({ attribute: 'size', reflect: true })
  size: 'small' | 'medium' | 'large' = 'medium';

  @property({ type: Boolean, attribute: 'outline', reflect: true })
  outline: boolean = false;

  @property({ type: Boolean, attribute: 'pill', reflect: true })
  pill: boolean = false;

  @property({ type: Boolean, attribute: 'circle', reflect: true })
  circle: boolean = false;

  // -----------------

  @state()
  reportsProvider?: ReportsAPI;

  @state()
  fileReferenceProvider?: FileReferenceAPI;

  @state()
  metadata?: FileReferenceMetadata;
  private _isFetchingMetadata: boolean = false;

  @state()
  fileReferenceID?: string;

  @state()
  status: RunnerStatus = 'idle'


  //#region Event Handlers

  public fileCheckInterval = new IntervalController(this, () => {
    (async () => {
      await this._updateFileMetadata();
    })();
  }, 2 * 1000); // 2 seconds

  private async _updateFileMetadata() {
    try {
      if (this.status !== 'running') return;
      if (this._isFetchingMetadata) return;
      if (this.fileReferenceProvider && this.fileReferenceID) {
        this._isFetchingMetadata = true;
        this.metadata = await this.fileReferenceProvider.GetFileReferenceMetadata(this.fileReferenceID);

        // Define "final" statuses
        const FINAL_STATUSES: FileReferenceClientStatus[] = ['InvalidFileReferenceID', 'Cancelled', 'TemporaryDowntime', 'Complete'];
        // If we were given back a "final" status that should mean we stop future checks
        if (this.metadata && FINAL_STATUSES.includes(this.metadata.Status)) {
          console.timeEnd(`Running report: ${this.fileReferenceID}`);
          // Regardless, we stop future checks
          this.fileCheckInterval.stopInterval();
          // Reset the button back after 5 seconds
          setTimeout(() => {
            this._reset();
          }, 5000);

          // If something good happened
          if (this.metadata.Status === 'Complete' && this.metadata.ContentDownloadUrl !== null) {
            this.status = 'completed';
            console.debug(`[${this.tagName}] Attempting to download: `, this.metadata.ContentDownloadUrl);
            /**
             * This is the best method for doing this that doesn't trigger popup blockers
             * Most browsers will download the file if there's `Content-Disposition` headers
             * without ever leaving the original page.
             */
            window.location.href = this.metadata.ContentDownloadUrl;
          }
          else {
            this.status = 'failed';
            console.error(`[${this.tagName}] Metadata did not indicate success: `, this.metadata);
          }
        }
      }
    }
    catch(err) {
      console.error(err);
      // Prevent endless checks if the API is having issues
      this.fileCheckInterval.stopInterval();
      this.status = 'failed';
    }
    finally {
      this._isFetchingMetadata = false;
    }
  }

  private _reset() {
    this.status = 'idle';
    this.metadata = undefined;
    this.fileReferenceID = undefined;
  }

  private async _handleClick(event: Event) {
    if (event instanceof PointerEvent && event.ctrlKey) return;
    if (!this.reportsProvider) return;
    if (!this.fileReferenceProvider) return;
    if (isNullishOrWhitespace(this.definition)) return;
    if (this.status !== 'idle') return;
    this.status = 'running';
    try {
      this.fileReferenceID = await this.reportsProvider.RunReport(this.definition);
      console.time(`Running report: ${this.fileReferenceID}`);
      this.fileCheckInterval.startInterval();
      await this._updateFileMetadata();
    }
    catch(err) {
      console.error(err);
    }
  }

  private async _handleContextMenu(event: Event) {
    if (event instanceof PointerEvent
      && event.ctrlKey
      && typeof navigator.clipboard?.writeText === 'function'
      && !isNullishOrWhitespace(this.definition)) {
        event.preventDefault();
        await navigator.clipboard.writeText(this.definition);
        this.animate([
          { transform: 'scale(1.05)' },
          { transform: 'scale(0.925)' },
          { transform: 'scale(1.0)' },
        ], {
          duration: 125,
          iterations: 1,
        });
    }
  }

  //#endregion

  //#region Renders

  render() {    
    //#region Effects

    // Whenever the API URL is updated, create a new provider
    useEffect(() => {
      try {
        const url = new URL(this.apiOrigin, window.origin);
        this.reportsProvider = new ReportsAPI(url);
        this.fileReferenceProvider = new FileReferenceAPI(url);
      }
      catch {}
    }, [this.apiOrigin]);

    //#endregion

    //#region Main Render

    return html`
      <sl-tooltip hoist ?disabled=${!(['running', 'failed', 'completed'] as RunnerStatus[]).includes(this.status)}>
        ${
          this.status === 'running'
          ? html`<span slot="content" style="font-style: italic;">Preparing document...</span>`
          : this.status === 'failed'
          ? html`<span slot="content">There was a problem preparing the document</span>`
          : this.status === 'completed'
          ? html`<span slot="content">Your document was prepared successfully and is downloading now</span>`
          : ''
        }
        <sl-button 
          style="width: 100%;"
          ?outline=${this.outline}
          ?pill=${this.pill}
          ?circle=${this.circle}
          ?loading=${this.status === 'running'}
          ?disabled=${isNullishOrWhitespace(this.definition)}
          .variant=${this.status === 'completed' ? 'success' : this.status === 'failed' ? 'danger' : 'primary'}
          .size=${this.size}
          href=${ifDefined(this.metadata?.ContentDownloadUrl ?? undefined)}
          target="_blank"
          @click=${this._handleClick}
          @contextmenu=${this._handleContextMenu}
          >
          ${
            this.status === 'completed'
            ? html`
              <!-- <sl-icon slot="prefix" name="check-lg"></sl-icon> -->
              <span slot="prefix" style="font-size: 1.5em;" class="text-white-outline">✅</span>
              `
            : this.status === 'failed'
            ? html`
              <!-- <sl-icon slot="prefix" name="exclamation-octagon"></sl-icon> -->
              <span slot="prefix" style="font-size: 1.5em;" class="text-white-outline">🛑</span>
              `
            : this.status === 'idle'
            ? html`
              <!-- <sl-icon slot="prefix" name="file-earmark-arrow-down"></sl-icon> -->
              <slot name="prefix" slot="prefix">
                <span style="font-size: 1.5em;" class="text-white-outline">🖨️</span>
              </slot>
              `
            : ''
          }
          <slot></slot>
        </sl-button>
      </sl-tooltip>
    `
    //#endregion
  }

  //#endregion

  //#region Styles
  static styles = [stylesBootstrap, css`
    :host {
      display: inline-block;
      width: auto;
      font-family: var(--bs-body-font-family);
      font-size: var(--bs-body-font-size);
      font-weight: var(--bs-body-font-weight);
      line-height: var(--bs-body-line-height);
      color: var(--bs-body-color);
      --font-mono: ui-monospace, SFMono-Regular, SF Mono, Menlo, "Cascadia Mono", Consolas, Liberation Mono, monospace;
    }
    .text-white-outline {
      text-shadow: -1px -1px 0 #fff,  
        1px -1px 0 #fff,
        -1px 1px 0 #fff,
        1px 1px 0 #fff;
    }
  `]
  //#endregion
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-report-runner': EPReportRunner
  }
}
